type Coords = {
  latitude: number;
  longitude: number;
  accuracy: number;
  capturedAt: number;
};

let cached: Coords | null = null;
let denied = false;
let inflight: Promise<Coords | null> | null = null;
let refreshTimer: ReturnType<typeof setInterval> | null = null;

const STALE_MS = 5 * 60 * 1000;
const REFRESH_INTERVAL_MS = 4 * 60 * 1000;

// Always returns whatever coords we have (even if stale), and lazily kicks
// off a refresh in the background when stale. Once the user has granted
// permission, we never go "back to nothing" — headers stay attached.
export const getCachedCoords = (): Coords | null => {
  if (!cached) return null;
  if (Date.now() - cached.capturedAt > STALE_MS) {
    requestGeolocation();
  }
  return cached;
};

export const requestGeolocation = (): Promise<Coords | null> => {
  if (denied) return Promise.resolve(null);
  if (inflight) return inflight;
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);

  inflight = new Promise<Coords | null>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        cached = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          capturedAt: Date.now(),
        };
        inflight = null;
        startBackgroundRefresh();
        resolve(cached);
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) denied = true;
        inflight = null;
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  });
  return inflight;
};

const startBackgroundRefresh = () => {
  if (refreshTimer) return;
  refreshTimer = setInterval(() => {
    if (!denied) requestGeolocation();
  }, REFRESH_INTERVAL_MS);
};

export const resetGeolocation = () => {
  cached = null;
  denied = false;
  inflight = null;
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
};
